/**
 * 요일·시간대 입력 — 캠페인 게재 시간대와 운영 시간이 같은 부품을 쓴다.
 *
 * 둘은 서버에서 같은 매칭 함수로 읽힌다(`@signageone/core` 의 `matchesDaypart`, 자정을 넘는 구간 포함).
 * 입력하는 모양도 같아야 "캠페인은 22:00~02:00 이 되는데 운영 시간은 안 된다" 같은 오해가 없다.
 */
import type { ReactNode } from 'react';
import { Badge } from './ui.js';
import { TimeField } from './TimeField.js';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export interface DaypartInput {
  days: number[];
  start: string;
  end: string;
}

export function DayPicker({ days, onChange }: { days: number[]; onChange: (days: number[]) => void }): ReactNode {
  return (
    <div className="daypicker" role="group" aria-label="요일">
      {DAY_LABELS.map((label, day) => (
        <button
          key={day}
          type="button"
          className={`day ${days.includes(day) ? 'day-on' : ''}`}
          aria-pressed={days.includes(day)}
          onClick={() =>
            onChange(days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b))
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** 운영 시간 규칙 목록 — 요일·시작·끝. 끝이 시작보다 이르면 다음 날까지다. */
export function HoursRulesEditor({
  rules,
  onChange,
  max = 14,
  addDefault = { days: [1, 2, 3, 4, 5], start: '07:00', end: '22:00' },
}: {
  rules: DaypartInput[];
  onChange: (rules: DaypartInput[]) => void;
  max?: number;
  addDefault?: DaypartInput;
}): ReactNode {
  const update = (index: number, patch: Partial<DaypartInput>): void =>
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  return (
    <div className="space-y-2">
      {rules.map((rule, index) => (
        <div key={index} className="rule-editor">
          <DayPicker days={rule.days} onChange={(days) => update(index, { days })} />
          {/* 적거나 시·분 목록에서 고른다(`TimeField`) — 브라우저 시각 칸은 사파리에 고를 목록이 없다. */}
          <TimeField value={rule.start} onChange={(start) => update(index, { start })} label="시작 시각" />
          {/*
            **자정은 00:00 으로 보인다.** 옛 서버·옛 저장값의 24:00 도 00:00 으로 그린다 — 00:00 은 끝이 시작보다
            이르니 다음 날 0시, 곧 자정까지와 같다(서버 규칙이 같다). "24:00" 을 적어도 00:00 으로 읽는다.
          */}
          <TimeField
            value={rule.end === '24:00' ? '00:00' : rule.end}
            onChange={(end) => update(index, { end })}
            label="종료 시각"
          />
          {rule.end === '24:00' || rule.end === '00:00' ? (
            <Badge tone="info">자정까지</Badge>
          ) : rule.end <= rule.start ? (
            <Badge tone="warn">익일까지</Badge>
          ) : null}
          <button type="button" className="link link-danger" onClick={() => onChange(rules.filter((_, i) => i !== index))}>
            제거
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn"
        disabled={rules.length >= max}
        onClick={() => onChange([...rules, { ...addDefault, days: [...addDefault.days] }])}
      >
        시간대 추가
      </button>
    </div>
  );
}
